import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, retry, switchMap, take, tap } from 'rxjs/operators';
import { EnvironmentService } from '../environment/environment.service';
import { ITenantLite } from './models/tenant-lite.model';
import { ITenant } from './models/tenant.model';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  public currentTenant = new BehaviorSubject<ITenant>(null);

  public tenant: ITenant = null;
  public  tenantListSignal = signal<ITenantLite[]>([]);
  public  tenantSignal = signal<ITenant | null>(null);
  public cloudStorageStatus: boolean = false;
  private networksListPageObject: any;
  private logger: Logger;

  private endpointGetTenantUser: string;
  private endpointFindTenant: string;

  public environmentDomain: string | null = null;
  public tenantDomain: string | null = null;
  public idpLoginToken: string | null = null;

  constructor(
    private environmentService: EnvironmentService,
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    private storageService: StorageService,

  ) {
    this.logger = this.loggingService.getLogger("[TenantService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  public setTenantInputValues(userInputTenantDomain: string | null, userInputEnvironmentDomain: string | null): void {
    this.logger.info('setSignInValues(): - ' + 'userInputTenantDomain: ' + userInputTenantDomain + ', userInputEnvironmentDomain: ' + userInputEnvironmentDomain);
    this.tenantDomain = userInputTenantDomain;
    this.environmentDomain = userInputEnvironmentDomain;
  }

  public setIdpLoginToken(idpLoginToken: string | null): void {
    this.storageService.addItemToLocalStorage('idpLoginToken', idpLoginToken, 'setIdpLoginToken()').subscribe(() => {
      this.logger.info('setIdpLoginToken() - idpLoginToken stored in local');
      this.idpLoginToken = idpLoginToken;
    });
  }

  private deserializeLinkObjects(tenant: ITenant, links: any): void {
    if (links) {
      if (links['self'] && links['self'].href) {
        tenant.links.self = links['self'].href;
      }
      if (links['px:currentuser'] && links['px:currentuser'].href) {
        tenant.links.currentUser = links['px:currentuser'].href;
      }
      if (links['px:printers'] && links['px:printers'].href) {
        tenant.links.printers = links['px:printers'].href;
      }
      if (links['px:queues'] && links['px:queues'].href) {
        tenant.links.queues = links['px:queues'].href;
      }
      if (links['px:commandReleasePrintJob'] && links['px:commandReleasePrintJob'].href) {
        tenant.links.releasePrintJob = links['px:commandReleasePrintJob'].href;
      }
      if (links['px:commandDeletePrintJob'] && links['px:commandDeletePrintJob']) {
        tenant.links.deletePrintJob = links['px:commandDeletePrintJob'].href;
      }
      if (links['px:networks'] && links['px:networks'].href) {
        tenant.links.networks = links['px:networks'].href;
      }
      if (links['px:workstations'] && links['px:workstations'].href) {
        tenant.links.workstations = links['px:workstations'].href;
      }
      if (links['px:workflows'] && links['px:workflows'].href) {
        tenant.links.workflows = links['px:workflows'].href;
      }
      if (links['px:airprintPasswords'] && links['px:airprintPasswords'].href) {
        tenant.links.airPrintPasswords = links['px:airprintPasswords'].href;
      }
      if (links['px:findPrinterByNfcTag'] && links['px:findPrinterByNfcTag'].href) {
        tenant.links.findPrinterByNfcTag = links['px:findPrinterByNfcTag'].href;
      }
      if (links['px:deviceLogUploads'] && links['px:deviceLogUploads'].href) {
        tenant.links.deviceLogUploads = links['px:deviceLogUploads'].href;
      }
      if (links['px:idCodes'] && links['px:idCodes'].href) {
        tenant.links.idCodes = links['px:idCodes'].href;
      }
      if (links['px:userDevices'] && links['px:userDevices'].href) {
        tenant.links.userDevices = links['px:userDevices'].href;
      }
    }
  }

  private deserializeEmbeddedObjects(tenant: ITenant, embedded: any): void {
    if (embedded) {
      for (let key in embedded) {
        tenant.embedded[key] = embedded[key];
      }
    }
  }

  private deserializeTenant(input: any): ITenant {
    if (input.hostingDomains) {
      let tenant: ITenant = {
        links: {
          airPrintPasswords: null,
          currentUser: null,
          deletePrintJob: null,
          deviceLogUploads: null,
          findPrinterByNfcTag: null,
          idCodes: null,
          networks: null,
          printers: null,
          queues: null,
          releasePrintJob: null,
          self: null,
          userDevices: null,
          workflows: null,
          workstations: null
        },
        embedded: {
          PUSHER_CLIENT_INFO: input._embedded.PUSHER_CLIENT_INFO ? input._embedded.PUSHER_CLIENT_INFO : null,
          authenticationConfiguration: input._embedded.authenticationConfiguration ? input._embedded.authenticationConfiguration : null,
          productsAndFeatures: input._embedded.productsAndFeatures ? input._embedded.productsAndFeatures : null,
          securePrintConfiguration: input._embedded.securePrintConfiguration ? input._embedded.securePrintConfiguration : null
        },
        tenantData: {
          mobilePrint: input.mobilePrint ? input.mobilePrint : false,
          // cloudStorageEnabled: this.cloudStorageStatus ? this.cloudStorageStatus : false,
          hostingDomain: input.hostingDomains[0] ? input.hostingDomains[0] : null,
          name: input.name ? input.name : null
        }
      };
      if (input._links) {this.deserializeLinkObjects(tenant, input._links);}
      return tenant;
    }
  }

  public refreshStoredTenant(): Observable<void> {
    this.logger.info('refreshStoredTenant()');
    return this.storageService.getItemFromLocalStorage('lastUsedTenant', 'getLastUsedTenant()').pipe(
      take(1),
      switchMap((lastUsedTenant: ITenantLite) => {
        return this.getTenantResource(lastUsedTenant.links.self).pipe(
          take(1),
          switchMap(() => {
            return of(null);
          })
        )
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('refreshStoredTenant() error:', error);
        return throwError(() => error);
      })
    );
  }

  public getTenantResource(tenantLink: string): Observable<ITenant> {
    this.logger.info('getTenantResource()');
    const embed = '?embed=PUSHER_CLIENT_INFO,securePrintConfiguration';
    return this.httpClient.get<any>(tenantLink + embed).pipe(
      retry(1),
      map((res: any) => {
        const tenant = this.deserializeTenant(res);
        this.tenantSignal.set(tenant);
        return tenant;
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('getTenantResource() error');
        return throwError(() => error);
      })
    );
  }

  public get freshTenant(): Observable<ITenant> {
    return this.currentTenant.asObservable();
  }

  /** TENANTLIST for multipe tenant select */
  public getTenantListForUser(): Observable<Array<ITenantLite>> {
    this.logger.info('getTenantListForUser()');

    return this.storageService.getItemFromLocalStorage('idpLoginToken', 'getTenantListForUser()').pipe(
      take(1),
      switchMap((idpLoginToken: string | null) => {
        this.endpointGetTenantUser = this.environmentService.getEnvironmentEndpoint['endpointGetTenantUser'];
        const idpLoginTokenParam = idpLoginToken ? '?idpLoginToken=' + idpLoginToken : '';
        const tenantListUrl = this.endpointGetTenantUser + idpLoginTokenParam;

        return this.httpClient.get<any>(tenantListUrl).pipe(
          map((tenantListResponse) => {
            const tenantListFormatted: ITenantLite[] = this.deserializeTenantList(tenantListResponse);
            this.tenantListSignal.set(tenantListFormatted);
            return tenantListFormatted;
          }),
          catchError((error: HttpErrorResponse) => {
            this.logger.error('getTenantListForUser() error');
            return throwError(() => error);
          })
        );
      })
    );
  }

  private deserializeTenantList(tenantsRaw: any): Array<ITenantLite> {
    this.logger.info('deserializeTenantList()');
    let tenantList: Array<ITenantLite> = [];
    for (let tenant of tenantsRaw._embedded.tenants) {
      let tenantLite: ITenantLite = {
        links: {
          self: tenant._links['px:tenant'].href,
          currentUser: tenant._links['px:tenant-user'].href,
        },
        tenantData: {
          name: tenant.name,
          hostingDomain: tenant.hostingDomain,
          intune: tenant.intune ? tenant.intune : null
        }
      };
      tenantList.push(tenantLite);
    }
    this.logger.info('deserializeTenantList() RETURN ');
    return tenantList;
  }

  public getTenantNetworks(netWorksEndpoint: string, storedNetworks: Array<string>, page: number, pageSize: number, sort: string, direction: string, searchString: string): Observable<Array<any>> {
    this.logger.info('getTenantNetworks()');
    let getNetworksUrl = netWorksEndpoint + '?page=' + page + '&pageSize=' + pageSize + '&sort=' + sort + '&direction=' + direction;

    if (searchString) {
      getNetworksUrl = getNetworksUrl + '&match=' + searchString;
    }

    if (storedNetworks.length > 0) {
      storedNetworks.forEach(nw => {
        getNetworksUrl += '&networkIds=' + nw;
      });
    }

    return this.httpClient.get<any>(getNetworksUrl).pipe(
        retry(1),
        tap((response: any) => {
          this.networksListPageObject = {
            page: response.page
          };
        }),
        map((response: any) => this.deserializeTenantNetworks(response)),
        catchError((error: HttpErrorResponse) => {
          this.logger.error('getTenantNetworks() error:', error);
          return throwError(() => error);
        })
      );
  }

  private deserializeTenantNetworks(input: any): Array<any> {
    let tenantNetworksList: Array<any> = [];

    if (input._embedded) {
      for (let network of input._embedded['px:networkResources']) {
        tenantNetworksList.push(this.deserializeNetwork(network));
      }
      tenantNetworksList.push(this.networksListPageObject);
    }

    return tenantNetworksList;
  }

  private deserializeNetwork(input: any): any {
    let network: any = {
      links: {
        self: input._links.self.href,
      },
      name: input.name
    };
    return network;
  }

  public resetTenant(): Observable<void> {
    this.logger.info('resetTenant()');
    return of(this.tenantSignal.set(null));
  }

  public checkIfUserTenantDomainExists(tenantDomain): Observable<any> {
    this.logger.info('checkIfUserTenantDomainExists()');
    this.endpointFindTenant = this.environmentService.getEnvironmentEndpoint['endpointFindTenant'];
    const url = this.endpointFindTenant + tenantDomain;
    return this.httpClient.get<any>(url).pipe(
      retry(1),
      map((response: any) => {
        return response;
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('checkIfUserTenantDomainExists() error');
        return throwError(() => error);
      })
    );
  }
}